import { useTranslation } from 'react-i18next';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  min?: number;
  max?: number;
  email?: boolean;
  url?: boolean;
  custom?: (value: any, formData?: any, context?: any) => string | null;
}

export interface ValidationErrors {
  [key: string]: string;
}

export interface FormValidationConfig {
  [fieldName: string]: ValidationRule;
}

// Common validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  URL: /^https?:\/\/.+/,
  ALPHANUMERIC: /^[a-zA-Z0-9\s]+$/,
  NUMERIC: /^\d+(\.\d+)?$/,
  POSITIVE_NUMBER: /^[1-9]\d*(\.\d+)?$/,
};

// Common validation rules
export const VALIDATION_RULES = {
  REQUIRED: { required: true },
  EMAIL: { required: true, email: true },
  PHONE: { required: true, pattern: VALIDATION_PATTERNS.PHONE },
  URL: { pattern: VALIDATION_PATTERNS.URL },
  NAME: { required: true, minLength: 2, maxLength: 100, pattern: VALIDATION_PATTERNS.ALPHANUMERIC },
  PASSWORD: { required: true, minLength: 6, maxLength: 128 },
  POSITIVE_NUMBER: { required: true, min: 0 },
  POSITIVE_INTEGER: { required: true, min: 1, pattern: /^\d+$/ },
  DECIMAL: { required: true, min: 0, pattern: VALIDATION_PATTERNS.NUMERIC },
  DATE: { required: true },
  TEXT: { maxLength: 500 },
  LONG_TEXT: { maxLength: 2000 },
};

export const useFormValidation = () => {
  const { t } = useTranslation();

  const validateField = (value: any, rules: ValidationRule, fieldName: string, formData?: any, context?: any): string | null => {
    // Handle empty values
    const isEmpty = value === null || value === undefined || value === '' || 
                   (Array.isArray(value) && value.length === 0);

    // Required validation
    if (rules.required && isEmpty) {
      return t(`${fieldName} is required`);
    }

    // Skip other validations if field is empty and not required
    if (isEmpty) {
      return null;
    }

    // Type-specific validations
    if (typeof value === 'string') {
      // Min length validation
      if (rules.minLength && value.length < rules.minLength) {
        return t(`${fieldName} must be at least ${rules.minLength} characters`);
      }

      // Max length validation
      if (rules.maxLength && value.length > rules.maxLength) {
        return t(`${fieldName} must be no more than ${rules.maxLength} characters`);
      }

      // Pattern validation
      if (rules.pattern && !rules.pattern.test(value)) {
        return t(`${fieldName} format is invalid`);
      }

      // Email validation
      if (rules.email && !VALIDATION_PATTERNS.EMAIL.test(value)) {
        return t('Please enter a valid email address');
      }

      // URL validation
      if (rules.url && !VALIDATION_PATTERNS.URL.test(value)) {
        return t('Please enter a valid URL');
      }
    }

    // Number validations
    if (typeof value === 'number' || !isNaN(Number(value))) {
      const numValue = Number(value);

      // Min value validation
      if (rules.min !== undefined && numValue < rules.min) {
        return t(`${fieldName} must be at least ${rules.min}`);
      }

      // Max value validation
      if (rules.max !== undefined && numValue > rules.max) {
        return t(`${fieldName} must be no more than ${rules.max}`);
      }
    }

    // Custom validation
    if (rules.custom) {
      const customError = rules.custom(value, formData, context);
      if (customError) {
        return t(customError);
      }
    }

    return null;
  };

  const validateForm = (formData: any, config: FormValidationConfig, context?: any): ValidationErrors => {
    const errors: ValidationErrors = {};

    Object.keys(config).forEach(fieldName => {
      const value = formData[fieldName];
      const rules = config[fieldName];
      const error = validateField(value, rules, fieldName, formData, context);
      
      if (error) {
        errors[fieldName] = error;
      }
    });

    return errors;
  };

  const validateFieldOnChange = (value: any, rules: ValidationRule, fieldName: string, formData?: any, context?: any): string | null => {
    return validateField(value, rules, fieldName, formData, context);
  };

  const clearFieldError = (errors: ValidationErrors, fieldName: string): ValidationErrors => {
    const newErrors = { ...errors };
    delete newErrors[fieldName];
    return newErrors;
  };

  const hasErrors = (errors: ValidationErrors): boolean => {
    return Object.keys(errors).length > 0;
  };

  const getFieldError = (errors: ValidationErrors, fieldName: string): string | undefined => {
    return errors[fieldName];
  };

  return {
    validateField,
    validateForm,
    validateFieldOnChange,
    clearFieldError,
    hasErrors,
    getFieldError,
    VALIDATION_RULES,
    VALIDATION_PATTERNS,
  };
};

// Predefined validation configurations for common forms
export const FORM_VALIDATION_CONFIGS = {
  USER: {
    name: VALIDATION_RULES.NAME,
    email: VALIDATION_RULES.EMAIL,
    password: VALIDATION_RULES.PASSWORD,
    role: VALIDATION_RULES.REQUIRED,
    tenantId: VALIDATION_RULES.REQUIRED,
    farmId: VALIDATION_RULES.REQUIRED,
  },
  FARM: {
    name: VALIDATION_RULES.NAME,
    location: { ...VALIDATION_RULES.REQUIRED, maxLength: 200 },
    size: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 10000 },
    status: { required: true, custom: (value: any) => {
      const validStatuses = ['active', 'inactive', 'maintenance'];
      if (!validStatuses.includes(value)) {
        return 'Status must be one of: active, inactive, maintenance';
      }
      return null;
    }},
    description: VALIDATION_RULES.TEXT,
    tenantId: VALIDATION_RULES.REQUIRED,
  },
  POND: {
    name: VALIDATION_RULES.NAME,
    type: VALIDATION_RULES.REQUIRED,
    area: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 1000000 },
    depth: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 100 },
    waterSource: VALIDATION_RULES.REQUIRED,
    capacity: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 10000000 },
    description: VALIDATION_RULES.TEXT,
    farmId: VALIDATION_RULES.REQUIRED,
  },
  FEED: {
    name: VALIDATION_RULES.NAME,
    description: VALIDATION_RULES.TEXT,
    quantityKg: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 1000000 },
    unitPrice: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 10000 },
    supplier: { maxLength: 200 },
    batchNumber: { maxLength: 100 },
    manufacturingDate: VALIDATION_RULES.DATE,
    expiryDate: VALIDATION_RULES.DATE,
  },
  ANIMAL: {
    name: VALIDATION_RULES.NAME,
    species: VALIDATION_RULES.REQUIRED,
    batchNumber: VALIDATION_RULES.REQUIRED,
    stockingDate: VALIDATION_RULES.DATE,
    initialCount: { ...VALIDATION_RULES.POSITIVE_INTEGER, max: 10000000 },
    initialWeightGrams: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 10000 },
    currentStage: VALIDATION_RULES.REQUIRED,
    farmId: VALIDATION_RULES.REQUIRED,
  },
  TENANT: {
    name: VALIDATION_RULES.NAME,
    theme: VALIDATION_RULES.REQUIRED,
    status: VALIDATION_RULES.REQUIRED,
    description: VALIDATION_RULES.TEXT,
    contactEmail: VALIDATION_RULES.EMAIL,
    contactPhone: { maxLength: 20 },
    address: { maxLength: 500 },
  },
  LOGIN: {
    email: VALIDATION_RULES.EMAIL,
    password: VALIDATION_RULES.REQUIRED,
  },
  FORGOT_PASSWORD: {
    email: VALIDATION_RULES.EMAIL,
  },
  RESET_PASSWORD: {
    newPassword: VALIDATION_RULES.PASSWORD,
    confirmPassword: {
      ...VALIDATION_RULES.REQUIRED,
      custom: (value: string, formData?: any) => {
        if (formData && value !== formData.newPassword) {
          return 'Passwords do not match';
        }
        return null;
      },
    },
  },
  WATER_QUALITY: {
    recordDate: VALIDATION_RULES.DATE,
    temperature: { min: -10, max: 50 },
    ph: { min: 0, max: 14 },
    dissolvedOxygen: { min: 0, max: 20 },
    turbidity: { min: 0, max: 1000 },
    ammonia: { min: 0, max: 10 },
    nitrite: { min: 0, max: 5 },
    nitrate: { min: 0, max: 100 },
    salinity: { min: 0, max: 50 },
    transparency: { min: 0, max: 100 },
    orp: { min: -1000, max: 1000 },
    notes: VALIDATION_RULES.TEXT,
  },
  FEED_RECORD: {
    feedDate: {
      ...VALIDATION_RULES.DATE,
      custom: (value: string, formData?: any, context?: any) => {
        if (context?.cycleStartDate && value && new Date(value) < new Date(context.cycleStartDate)) {
          return 'Feed date cannot be earlier than the cycle start date';
        }
        return null;
      }
    },
    feed1: { min: 0, max: 10000 },
    feed2: { min: 0, max: 10000 },
    feed3: { min: 0, max: 10000 },
    feed4: { min: 0, max: 10000 },
    notes: VALIDATION_RULES.TEXT,
  },
  MORTALITY_RECORD: {
    date: VALIDATION_RULES.DATE,
    age: { min: 0, max: 1000 },
    basedOn: VALIDATION_RULES.REQUIRED,
    quantity: { min: 0, max: 1000000 },
    weight: { min: 0, max: 10000 },
    size: { min: 0, max: 1000 },
    multiplier: { min: 0.1, max: 100 },
    notes: VALIDATION_RULES.TEXT,
  },
  CYCLE: {
    startedAt: VALIDATION_RULES.DATE,
    finishedAt: { 
      custom: (value: string, formData?: any) => {
        if (formData && formData.startedAt && value && new Date(value) <= new Date(formData.startedAt)) {
          return 'Finished date must be after start date';
        }
        return null;
      }
    },
    totalSeed: { ...VALIDATION_RULES.POSITIVE_INTEGER, max: 10000000 },
    seedType: { 
      required: true, 
      custom: (value: any) => {
        const validTypes = ['hatchery', 'wild', 'broodstock', 'other'];
        if (!validTypes.includes(value)) {
          return 'Seed type must be one of: hatchery, wild, broodstock, other';
        }
        return null;
      }
    },
    notes: VALIDATION_RULES.TEXT,
  },
  HARVEST_RECORD: {
    harvestDate: VALIDATION_RULES.DATE,
    count: { ...VALIDATION_RULES.POSITIVE_INTEGER, max: 10000000 },
    totalWeight: { ...VALIDATION_RULES.POSITIVE_NUMBER, max: 1000000 },
    harvestType: { 
      required: true, 
      custom: (value: any) => {
        const validTypes = ['partial', 'final'];
        if (!validTypes.includes(value)) {
          return 'Harvest type must be either partial or final';
        }
        return null;
      }
    },
    notes: VALIDATION_RULES.TEXT,
  },
}; 